import { Box, CircularProgress } from "@mui/material";
import { PAGES } from "./components/constants";
import {
  Route,
  Navigate,
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
} from "react-router-dom";
import { Suspense, lazy } from "react";
import * as Sentry from "@sentry/react";

import "./styles/App.css";
import SignIn from "./components/Auth/SignIn";
import Navibar from "./components/Navibar";
import GenericErrorDisplay from "./components/ErrorComponents/GenericErrorDisplay";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { V2SiteSummaryRoute } from "./components/Quantifiers/V2SiteSummary";
import { SignedIn, SignedOut } from "@clerk/clerk-react";

const Main = lazy(() => import("./components/Main"));

const EMSTheme = createTheme({
  palette: {
    primary: {
      main: "#006D3B",
    },
    secondary: {
      main: "#C5932C",
    },
  },
});

// Call this AFTER Sentry.init() which is done in utils/sentry.js
const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

function App() {
  const MainPage = ({ targetPage }) => {
    return <Main page={targetPage} />;
  };

  const routePaths = [
    PAGES.TESTING_BOARDS,
    PAGES.CALIBRATION,
    PAGES.HISTORY,
    PAGES.GATEWAYS,
    PAGES.PLANS_OPTIMIZATION,
    PAGES.MANAGE,
  ];

  const router = sentryCreateBrowserRouter(
    createRoutesFromElements(
      <Route
        element={
          <>
            <SignedIn>
              <Navibar />
            </SignedIn>
            <SignedOut>
              <SignIn />
            </SignedOut>
          </>
        }
      >
        <Route
          key="/"
          exact
          path="/"
          element={<Navigate to={PAGES.TESTING_BOARDS} />}
        />
        {routePaths.map((path) => (
          <Route
            key={path}
            path={path}
            element={<MainPage targetPage={path} />}
          />
        ))}
        <Route path={PAGES.SITES} lazy={() => import("./components/Sites")} />
        <Route
          path={`${PAGES.QUANTIFIER}`}
          lazy={() => import("./components/Quantifiers")}
        >
          <Route path=":siteId" element={<V2SiteSummaryRoute />} />
        </Route>

        <Route
          path={`${PAGES.FAILED_SENSORS}/:date?`}
          lazy={() => import("./components/FailedSensors")}
        />
        <Route
          path={PAGES.DEPLOYMENTS}
          lazy={() => import("./components/Deployments")}
        />
        <Route
          path={PAGES.NOTES_SITE_FIELD}
          lazy={() => import("./components/FieldSite")}
        />
        <Route
          path={PAGES.NOTES_DISTRIBUTORS}
          lazy={() => import("./components/Distributors")}
        />
        <Route path={PAGES.USERS}>
          <Route path="" index lazy={() => import("./components/Users")} />
          <Route
            path={`new`}
            lazy={() => import("./components/Users/AddNewUser")}
          />
          <Route
            path={":user_id"}
            lazy={() => import("./components/Users/UserPage")}
          />
        </Route>
      </Route>,
    ),
  );

  return (
    <div className="App">
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <ThemeProvider theme={EMSTheme}>
          <Suspense
            fallback={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mt: 5,
                  mb: 5,
                }}
              >
                <CircularProgress
                  size={48}
                  role="status"
                  aria-label="Loading..."
                />
              </Box>
            }
          >
            <Sentry.ErrorBoundary fallback={<GenericErrorDisplay />}>
              <RouterProvider router={router} />
            </Sentry.ErrorBoundary>
          </Suspense>
        </ThemeProvider>
      </LocalizationProvider>
    </div>
  );
}

export default App;
